import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgLabelTemplateDirective,
  NgOptionComponent,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import { RecaptchaV3Module, ReCaptchaV3Service } from 'ng-recaptcha-2';
import { HttpContext } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';

import { RegistrationCoreConfig } from '../registration/registration-core-config.interface';
import { LandingConfigService } from './landing-config.service';
import { TTPOI_ORIGIN } from '../../embed/embed-origin';
import { SubmitResponse } from '../../types/submit-form.type';
import { FormRegistrationModel } from '../../models/form-registration.model';
import { ClientService } from '../../core/services/client.service';
import { QueryListService } from '../../core/services/query-list.service';
import { MerchantService } from '../../core/services/merchant.service';
import { AuthService } from '../../core/services/authentication/auth.service';
import { SweetAlertService } from '../../core/services/sweet-alert.service';
import { LoaderService } from '../../core/services/loader.service';
import { FormToggleService } from '../../core/services/form-toggle.service';
import { WindowRefService } from '../../core/services/window-ref.service';
import { RETRY_ON_AUTH_FAILURE } from '../../core/services/authentication/auth.interceptor';
import { CountryModel } from '../../models/country.model';
import { MseModel } from '../../models/mse.model';
import { countrySample, mseSample } from '../../core/data/default.data';
import { Search } from '../../core/helpers/search';
import { FlagEmojiPipe } from '../pipes';
import GlobalComponent from '../../global-component';
import { environment } from '../../../environments/environment';
import { SharedPolicyModalComponent } from '../components/shared-policy-modals/shared-policy-modal.component';
import { PolicyOverlayComponent } from './policy-overlay.component';
import {
  TermsOfServiceTemplateComponent,
} from '../ui/agreements/terms-of-service-template.component';
import {
  PrivacyNoticeTemplateComponent,
} from '../ui/agreements/privacy-notice-template.component';
import { ConsentService } from '../tracking/consent.service';
import { AttributionService } from '../tracking/attribution.service';
import { DedupIdService } from '../tracking/dedup-id.service';
import { AppConsentService } from '../tracking/app-consent.service';

/**
 * Shared registration core component used by both main-app routes and the
 * embedded Angular Element (Shadow DOM).
 *
 * ### Shadow DOM overlay strategy
 *
 * **ng-select:** All ng-select instances render their dropdowns inline (no
 * `appendTo` is set). This is the default ng-select behaviour and ensures
 * dropdowns remain inside the shadow root, inheriting scoped styles correctly.
 *
 * **SweetAlert2:** In embed mode (`config.useOverlayPolicies === true`), policy
 * dialogs use `PolicyOverlayComponent` (native `<dialog>`) instead of
 * `SharedPolicyModalComponent`. For submit-related alerts the component still
 * injects `SweetAlertService`; however, in the Angular Element build the host
 * provides `EmbedDialogService` (via the `DialogService` token) which uses the
 * native `<dialog>` top-layer API — no dependency on SweetAlert2 DOM globals.
 * This ensures all overlays render within or above the shadow root without
 * appending to `document.body`.
 */
@Component({
  selector: 'app-registration-core',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RecaptchaV3Module,
    NgSelectComponent,
    NgOptionComponent,
    NgOptionTemplateDirective,
    NgLabelTemplateDirective,
    FlagEmojiPipe,
    TranslatePipe,
    SharedPolicyModalComponent,
    PolicyOverlayComponent,
    TermsOfServiceTemplateComponent,
    PrivacyNoticeTemplateComponent,
  ],
  templateUrl: './registration-core.component.html',
  styles: [`
    :host { display: contents; }
    :host(.landing-mode) {
      display: block;
      position: relative;
    }
    .widget-loader-overlay {
      position: absolute;
      inset: 0;
      z-index: 1000;
      display: grid;
      place-items: center;
      pointer-events: auto;
      border-radius: inherit;
    }
    .widget-loader-backdrop {
      position: absolute;
      inset: 0;
      background: rgba(255, 255, 255, 0.75);
      border-radius: inherit;
    }
    .widget-loader-content {
      position: relative;
      z-index: 1;
      text-align: center;
    }
    .widget-spinner {
      width: 2.5rem;
      height: 2.5rem;
      border: 0.3rem solid rgba(0, 0, 0, 0.1);
      border-top-color: #0d6efd;
      border-radius: 50%;
      animation: widget-spin 0.8s linear infinite;
      margin: 0 auto;
    }
    .widget-loader-text {
      margin-top: 0.75rem;
      font-size: 0.875rem;
      color: #495057;
      font-weight: 500;
    }
    @keyframes widget-spin {
      to { transform: rotate(360deg); }
    }
  `],
  providers: [LandingConfigService],
})
export class RegistrationCoreComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() config: RegistrationCoreConfig = {};
  @Output() submitted = new EventEmitter<SubmitResponse>();
  @Output() failed = new EventEmitter<unknown>();

  /** Adds 'landing-mode' class to the host element in landing mode, enabling position:relative for the scoped loader. */
  @HostBinding('class.landing-mode') get isLandingMode(): boolean {
    return this.config.isLanding === true;
  }

  // --- DI ---
  private clientService = inject(ClientService);
  private queryListService = inject(QueryListService);
  private api = inject(MerchantService);
  private auth = inject(AuthService);
  private sweet = inject(SweetAlertService);
  private cdr = inject(ChangeDetectorRef);
  private loader = inject(LoaderService);
  private toggler = inject(FormToggleService);
  private recaptchaV3 = inject(ReCaptchaV3Service);
  private platformId = inject(PLATFORM_ID);
  private winRef = inject(WindowRefService);
  protected translate = inject(TranslateService);
  private landingConfig = inject(LandingConfigService);

  /**
   * Frontend origin serving static assets, injected only in the embed build.
   * Empty on the normal SSR routes (assets are same-origin there).
   */
  private ttpoiOrigin = inject(TTPOI_ORIGIN, { optional: true }) ?? '';

  // --- Phase 2 tracking (optional — only provided in embed/landing context) ---
  private consentService = inject(ConsentService, { optional: true });
  private attributionService = inject(AttributionService, { optional: true });
  private dedupIdService = inject(DedupIdService, { optional: true });

  // First-party GA4 consent for the main SSR site — granted when the user
  // accepts the Privacy Notice. Root-provided, so always resolvable.
  private appConsent = inject(AppConsentService, { optional: true });

  // --- State ---
  /** Resolved client identifier — from config or ClientService. */
  clientId: string = '';

  /** Auth readiness flag */
  authReady = false;

  /** Captcha token */
  recaptchaToken: string | null = null;

  /** Default country (Malaysia). */
  countryIdCurrent: number = 132;
  countryCurrent: string = 'MY';

  // --- Submission state ---
  submitting = false;
  submitSuccess?: boolean;
  submitError?: string;

  // --- Country data ---
  countries: CountryModel[] = countrySample;
  countriesFiltered: CountryModel[] = countrySample;

  // --- Dropdown/States loading ---
  dropdown: any = null;
  statesList: { id: number; name: string; country_id: number; country_code: string; is_enabled: boolean }[] = [];
  statesListFiltered: { id: number; name: string; country_id: number; country_code: string; is_enabled: boolean }[] = [];
  mdrRates: any[] = [];

  // --- MSE (Merchant Sales Executive) data ---
  mseEnabled = false;
  mseList: MseModel[] = mseSample;
  mseListFiltered: MseModel[] = mseSample;
  mseSelected: MseModel | null = null;
  ownedBy: string = '';

  // --- Agreement state ---
  viewed = {
    privacyNotice: false,
    termsOfService: false,
  };
  is_read_tos = false;
  is_read_pl = false;
  agreeErrors: Record<string, string> = {
    privacyNotice: '',
    termsOfService: '',
  };

  /** Error shown if user clicks userAgreement too early */
  userAgreementError = '';

  /** Status message showing which documents have been reviewed */
  agreementStatusMessage = '';

  /** Tracks which agreement modal is currently open (for scroll detection) */
  currentAgreementOpened: 'privacyNotice' | 'termsOfService' | null = null;

  /** Accordion open state */
  accOpen = true;

  // --- Policy modal template refs ---
  modalTitle = '';
  modalTpl: TemplateRef<any> | null = null;
  @ViewChild('tosTpl') tosTpl!: TemplateRef<any>;
  @ViewChild('ppTpl') ppTpl!: TemplateRef<any>;
  @ViewChild(SharedPolicyModalComponent) policyModal!: SharedPolicyModalComponent;
  @ViewChild('policyOverlay') policyOverlay!: PolicyOverlayComponent;

  /** Whether MSE is visible in landing mode (toggleable at any time). Default true. */
  get showMseInLanding(): boolean {
    // Only relevant when isLanding + ttpoi-web
    if (!this.config.isLanding || this.clientId !== 'ttpoi-web') return false;
    return this.config.showMseInLanding !== false; // default true
  }

  // --- Search helper ---
  private search = new Search();

  /** The registration form model with all controls. */
  createSubmitForm: FormGroup<FormRegistrationModel> = new FormGroup({
    existingMerchant: new FormControl<'Yes' | 'No'>('No', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    country: new FormControl<number | null>(
      { value: 132, disabled: true },
      { validators: [] }
    ),
    companyName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(255)],
    }),
    registerEmail: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    // CelcomDigi-specific
    registerBy: new FormControl<'online' | 'dealer' | null>(null),
    stateSelected: new FormControl<string | null>(null),
    dealerContact: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(/^\+?[0-9]*$/), Validators.minLength(8), Validators.maxLength(20)],
    }),
    // Agreements
    userAgreement: new FormControl<boolean>(false, { nonNullable: true }),
    termsOfService: new FormControl<boolean>(false, { nonNullable: true }),
    privacyNotice: new FormControl<boolean>(false, { nonNullable: true }),
  });

  // ========== Asset resolution ==========

  /**
   * Resolves a relative asset path to an absolute URL.
   *
   * In the embed build the widget is hosted on a third-party page, so relative
   * paths like `assets/images/icons/x.svg` would resolve against the host page.
   * When `ttpoiOrigin` is set (embed only), prepend it so assets load from the
   * TTPOI frontend. On normal SSR routes `ttpoiOrigin` is empty and the original
   * relative path is returned unchanged.
   *
   * @param path a path relative to the app root, e.g. "assets/images/icons/company_box.svg"
   */
  asset(path: string): string {
    if (!this.ttpoiOrigin) {
      return path;
    }
    const cleanPath = path.replace(/^\/+/, '');
    return `${this.ttpoiOrigin}/${cleanPath}`;
  }

  // ========== Lifecycle ==========

  async ngOnInit(): Promise<void> {
    console.log('Version', { version: GlobalComponent.APP_VERSION, build_timestamp: GlobalComponent.APP_TIMESTAMP });

    // Resolve clientId: prefer config, fall back to ClientService
    this.clientId = this.config.clientId ?? this.clientService.clientId;

    // In landing mode, suppress the global full-viewport loader overlay.
    // The widget-local loading indicator (scoped to the component bounds) is used instead.
    if (this.config.isLanding) {
      this.loader.suppress();
    }

    // Configure landing config service, then pin the resolved clientId so the
    // outgoing `source` defaults correctly on non-landing routes.
    this.landingConfig.configure(this.config);
    this.landingConfig.clientId = this.clientId;

    // --- Auth initialization ---
    try {
      const tokenPayload = { client_id: this.clientId };
      if (!this.auth.getAccessToken?.() || !this.auth.isAuthenticated?.()) {
        await this.auth.fetchInitialAccessToken(tokenPayload);
      }
      this.authReady = true;
    } catch (e) {
      this.auth.useFallbackToken();
      this.authReady = true;
      console.warn('Initial token fetch failed; proceeding without token.', e);
    }

    // --- CelcomDigi-specific validator wiring ---
    if (this.clientId === 'celcomdigi') {
      this.createSubmitForm.get('registerBy')?.setValidators([Validators.required]);
      this.createSubmitForm.get('stateSelected')?.setValidators([Validators.required]);
      this.createSubmitForm.get('dealerContact')?.setValidators([
        Validators.required,
        Validators.pattern(/^\+?[0-9]*$/),
        Validators.minLength(8),
        Validators.maxLength(20),
      ]);
      this.createSubmitForm.get('registerBy')?.updateValueAndValidity();
      this.createSubmitForm.get('stateSelected')?.updateValueAndValidity();
      this.createSubmitForm.get('dealerContact')?.updateValueAndValidity();

      // Initially disable stateSelected and dealerContact until "Dealer" is selected
      this.createSubmitForm.get('stateSelected')?.disable({ emitEvent: false });
      this.createSubmitForm.get('dealerContact')?.disable({ emitEvent: false });

      // Listen to registerBy changes to toggle stateSelected and dealerContact
      this.createSubmitForm.get('registerBy')?.valueChanges.subscribe(val => {
        if (val?.toLowerCase() === 'dealer') {
          this.createSubmitForm.get('stateSelected')?.enable({ emitEvent: false });
          this.createSubmitForm.get('dealerContact')?.enable({ emitEvent: false });
        } else {
          this.createSubmitForm.get('stateSelected')?.disable({ emitEvent: false });
          this.createSubmitForm.get('dealerContact')?.disable({ emitEvent: false });
          this.createSubmitForm.get('stateSelected')?.reset(null, { emitEvent: false });
          this.createSubmitForm.get('dealerContact')?.reset('', { emitEvent: false });
        }
      });
    }

    // --- Agreement validators (all clients) ---
    this.createSubmitForm.get('userAgreement')?.setValidators([Validators.requiredTrue]);
    this.createSubmitForm.get('termsOfService')?.setValidators([Validators.requiredTrue]);
    this.createSubmitForm.get('privacyNotice')?.setValidators([Validators.requiredTrue]);
    this.createSubmitForm.get('userAgreement')?.updateValueAndValidity();
    this.createSubmitForm.get('termsOfService')?.updateValueAndValidity();
    this.createSubmitForm.get('privacyNotice')?.updateValueAndValidity();

    // --- Load dropdown data for celcomdigi ---
    if (this.clientId === 'celcomdigi') {
      await this.getDropdown();
    }

    // --- Load MSE list (ttpoi-web only) ---
    if (this.clientId === 'ttpoi-web') {
      await this.getMseDropdown();
    }

    // --- Disable country selector ---
    this.createSubmitForm.get('country')?.disable({ emitEvent: false });
    this.toggler.captureInitiallyDisabled(this.createSubmitForm);

    // --- existingMerchant valueChanges subscription (ttpoi-web only, non-landing) ---
    if (this.clientId === 'ttpoi-web' && !this.config.isLanding) {
      this.createSubmitForm.get('existingMerchant')!
        .valueChanges
        .subscribe(async (val: 'Yes' | 'No') => {
          if (val === 'Yes') {
            await this.sweet.infoHtml(
              this.translate.instant('registration.submitForm.info.title'),
              this.translate.instant('registration.submitForm.info.message')
            );
            this.setFormDisabled(true);
          } else {
            this.setFormDisabled(false);
          }
        });
    }
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    setTimeout(() => {
      const g = this.winRef.grecaptcha;
      console.log('grecaptcha present:', !!g);
      console.log('grecaptcha.execute present:', !!g?.execute);
      console.log('grecaptcha.enterprise.execute present:', !!g?.enterprise?.execute);
    }, 0);
  }

  ngOnDestroy(): void {
    // Restore global loader if it was suppressed in landing mode
    if (this.config.isLanding) {
      this.loader.unsuppress();
    }
  }

  // ========== Form helpers ==========

  setFormDisabled(disabled: boolean): void {
    this.toggler.setDisabled(this.createSubmitForm, disabled, {
      skip: ['existingMerchant', 'country'],
    });
  }

  hasError(field: string, error?: string): boolean {
    const ctrl = this.createSubmitForm.get(field);
    if (!ctrl) return false;
    return ctrl.touched && (error ? ctrl.hasError(error) : ctrl.invalid);
  }

  // ========== Submit orchestration ==========

  async onSubmit(): Promise<void> {
    this.submitting = true;
    this.submitSuccess = undefined;
    this.submitError = undefined;
    const formValue = this.createSubmitForm.value as any;
    formValue.country = this.countryIdCurrent;

    if (this.createSubmitForm.invalid) {
      this.createSubmitForm.markAllAsTouched();

      // Set agreement error if userAgreement is invalid
      if (this.createSubmitForm.get('userAgreement')?.invalid) {
        const unread: string[] = [];
        if (!this.viewed.privacyNotice) unread.push('Privacy Notice');
        if (!this.viewed.termsOfService) unread.push('Terms of Service');
        this.userAgreementError = unread.length > 0
          ? `Please read and agree to the ${unread.join(' and ')}.`
          : 'Please read and agree to the Privacy Notice and Terms of Service.';
      }

      Object.keys(this.createSubmitForm.controls).forEach(key => {
        const ctrl = this.createSubmitForm.get(key);
        if (ctrl?.invalid) {
          console.warn(`[Registration] Invalid control: "${key}"`, ctrl.errors, `(disabled: ${ctrl.disabled})`);
        }
      });

      await this.sweet.errorHtml(
        this.translate.instant('registration.submitForm.error.title'),
        this.translate.instant('registration.submitForm.error.message'),
        'OK'
      );
      this.submitting = false;
      return;
    }

    try {
      this.recaptchaToken = await firstValueFrom(
        this.recaptchaV3.execute('registration_submit').pipe(timeout(10000))
      );

      // Build payload
      const includeMse = this.clientId === 'ttpoi-web' && (!this.config.isLanding || this.showMseInLanding);
      const mso = includeMse && this.mseEnabled && this.mseSelected ? this.mseSelected.capture_username : null;
      const payload: Record<string, any> = {
        is_existing: formValue.existingMerchant === 'Yes',
        country_id: this.countryIdCurrent,
        country: this.countryCurrent,
        company_name: formValue.companyName,
        email: formValue.registerEmail,
        recaptcha_token: this.recaptchaToken,
        client_id: this.clientId,
      };

      // Include owned_by/mso only when MSE is visible (non-landing always, landing only when showMseInLanding is true)
      if (includeMse) {
        payload['owned_by'] = this.ownedBy || mso;
        payload['mso'] = mso;
      }

      // Agreements
      payload['is_read_tos'] = this.is_read_tos;
      payload['is_read_pl'] = this.is_read_pl;

      // CelcomDigi-specific fields
      if (this.clientId === 'celcomdigi') {
        payload['register_by'] = formValue.registerBy ?? '';
        payload['state'] = formValue.registerBy.toLowerCase() === 'online'
          ? 'Kuala Lumpur'
          : (formValue.stateSelected ?? '');
        payload['dealer_contact'] = formValue.dealerContact ?? '00000000';
      }

      // Add is_landing and source (additive).
      // source is `partner-campaign` for the landing page, otherwise the clientId.
      const landingExtension = this.landingConfig.buildPayloadExtension();
      payload['is_landing'] = landingExtension.is_landing;
      payload['source'] = landingExtension.source;

      // Phase 2: Add tracking data when in landing mode and consent is granted.
      // Only transmit attribution when consent is granted (Req 4.5).
      // GA4 is the only tracking layer — attribution is used for GA4 event parameters
      // and stored server-side for marketing reporting.
      if (this.config.isLanding && this.consentService && this.attributionService && this.dedupIdService) {
        const consentState = this.consentService.state;
        const dedupId = this.dedupIdService.generate();
        payload['consent'] = consentState;
        payload['event_dedup_id'] = dedupId;

        if (consentState === 'granted') {
          payload['attribution'] = this.attributionService.getAttribution();
        }
      }

      const context = new HttpContext().set(RETRY_ON_AUTH_FAILURE, true);

      const result = await this.api.submitRegistration(payload, { context, retryOnAuthFailure: true }) as SubmitResponse;

      if (!result?.status) {
        const fallbackMsg = this.translate.instant('registration.submitForm.submitError.message');
        const code = result?.code ?? '';
        const errorCodeMsg = this.translate.instant('registration.submitForm.submitError.errorCode');
        const msg = code
          ? (fallbackMsg + "<br><span class='error-code'>" + errorCodeMsg + code + '</span>')
          : fallbackMsg;
        this.submitSuccess = false;
        this.submitting = false;
        this.submitError = msg;
        this.failed.emit(result);
        await this.sweet.errorHtml(
          this.translate.instant('registration.submitForm.error.title'),
          msg,
          'OK'
        );
        return;
      } else if (result?.status) {
        await this.sweet.successHtml(
          this.translate.instant('registration.submitForm.success.title'),
          this.translate.instant('registration.submitForm.success.message'),
          'OK'
        );

        this.submitting = false;
        this.submitSuccess = true;
        this.submitted.emit(result);

        this.createSubmitForm.reset({
          existingMerchant: 'No',
          country: this.countryIdCurrent,
        });
      }
    } catch (err: any) {
      if (err instanceof TimeoutError) {
        // reCAPTCHA timeout — preserve form data, show specific error
        this.submitting = false;
        this.submitError = 'reCAPTCHA verification could not be completed. Please try again.';
        await this.sweet.errorHtml(
          this.translate.instant('registration.submitForm.error.title'),
          'reCAPTCHA verification could not be completed. Please try again.',
          'OK'
        );
        return;
      }
      this.submitSuccess = false;
      this.submitError =
        err?.error?.message ||
        err?.message ||
        this.translate.instant('alerts.error.message');
      this.failed.emit(err);
      await this.sweet.errorHtml(
        this.translate.instant('registration.submitForm.error.title'),
        this.translate.instant('registration.submitForm.error.message'),
        'OK'
      );
      this.submitting = false;
      return;
    } finally {
      this.recaptchaToken = null;
      this.submitting = false;
    }
    this.submitting = false;
  }

  // ========== Existing Merchant handler ==========

  async onExistingMerchantChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (input?.value === 'Yes' && input.checked) {
      await this.sweet.info(
        this.translate.instant('registration.submitForm.info.title'),
        this.translate.instant('registration.submitForm.info.message')
      );
      this.setFormDisabled(true);
    } else if (input.value === 'No' && input.checked) {
      this.setFormDisabled(false);
    }
  }

  // ========== Country handling ==========

  private findCountryById(id: number | null): CountryModel | undefined {
    if (!id) return undefined;
    return this.countries.find(c => c.id === id && c.is_enabled);
  }

  protected updateSelectionFromId(type: 'country', event: any): void {
    const rawId = event?.id;
    if (rawId == null || rawId === '') {
      if (type === 'country') this.countryCurrent = '';
      return;
    }

    const idStr: string = String(rawId);
    const idNum: number | null = typeof rawId === 'number'
      ? rawId
      : Number.isFinite(Number(rawId))
        ? Number(rawId)
        : null;

    let match: any;

    switch (type) {
      case 'country':
        match = idNum != null
          ? this.countries.find((item: { id: any }) => Number(item.id) === idNum)
          : this.countries.find((item: { id: any }) => String(item.id) === idStr);
        this.countryCurrent = match?.iso2 != null ? String(match.iso2) : '';

        if (idNum != null) {
          const selectedCountry = this.findCountryById(idNum);
          this.countriesFiltered = selectedCountry
            ? [selectedCountry]
            : this.countries.filter((c: any) => c.is_enabled);
        } else {
          this.countriesFiltered = this.countries.filter((c: any) => c.is_enabled);
        }
        break;
    }
  }

  // ========== Dropdown/States loading ==========

  async getDropdown(): Promise<void> {
    try {
      this.dropdown = await this.queryListService.getDropdown([1, 7], this.clientId);
      const dropdownData = this.dropdown?.data || {};

      const states = dropdownData.states ?? [];
      this.statesList = states
        .filter((s: any) => s.is_enabled)
        .map((s: any) => ({
          id: s.id,
          name: s.name,
          country_id: s.country_id,
          country_code: s.country_code,
          is_enabled: s.is_enabled,
        }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      this.statesListFiltered = [...this.statesList];

      this.mdrRates = dropdownData.mdr_rates ?? [];
    } catch (e) {
      console.warn('Failed to load dropdown data:', e);
    }
  }

  // ========== MSE (Merchant Sales Executive) ==========

  async getMseDropdown(): Promise<void> {
    try {
      const clientIdLower = this.clientId.toLowerCase();
      const resp = await this.queryListService.getDropdown([7, 8], clientIdLower);
      const dropdownData = resp?.data || {};

      const clients = dropdownData.clients ?? [];
      if (Array.isArray(clients) && clients.length > 0) {
        this.ownedBy = clients[0].owned_by ?? '';
      }

      const mseData = dropdownData.merchantSalesOfficers ?? [];
      if (Array.isArray(mseData) && mseData.length > 0) {
        this.mseList = mseData.map((m: any) => ({
          id: m.id,
          client_id: m.clientId,
          first_name: m.firstName,
          last_name: m.lastName ?? null,
          display_name: m.displayName,
          user_id: m.userId,
          capture_username: m.captureUsername,
          is_enabled: m.isEnabled ?? true,
        }));
        this.mseListFiltered = [...this.mseList];
      }
    } catch (e) {
      console.warn('Failed to load MSE data:', e);
    }
  }

  onMseToggle(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.mseEnabled = input.checked;
    if (!this.mseEnabled) {
      this.mseSelected = null;
    }
  }

  onMseRadioChange(enabled: boolean): void {
    this.mseEnabled = enabled;
    if (!enabled) {
      this.mseSelected = null;
    } else {
      this.mseListFiltered = [...this.mseList];
    }
  }

  onMseSelected(event: MseModel | null): void {
    this.mseSelected = event ?? null;
  }

  // ========== Agreement handlers ==========

  private markViewed(name: keyof typeof this.viewed): void {
    this.viewed[name] = true;
    this.agreeErrors[name] = '';
  }

  private updateAgreementError(): void {
    const ppDone = this.viewed.privacyNotice;
    const tosDone = this.viewed.termsOfService;

    if (ppDone && tosDone) {
      this.agreementStatusMessage = '<span class="text-success"><i class="bi bi-check-circle-fill me-1"></i>Privacy Notice and Terms of Service done</span>';
      this.userAgreementError = '';
    } else if (ppDone && !tosDone) {
      this.agreementStatusMessage =
        '<span class="text-success"><i class="bi bi-check-circle-fill me-1"></i>Privacy Notice done</span>' +
        '<br><span class="text-muted">Please read the Terms of Service</span>';
    } else if (!ppDone && tosDone) {
      this.agreementStatusMessage =
        '<span class="text-success"><i class="bi bi-check-circle-fill me-1"></i>Terms of Service done</span>' +
        '<br><span class="text-muted">Please read the Privacy Notice</span>';
    } else if (!ppDone && !tosDone) {
      if (!this.agreementStatusMessage) {
        this.agreementStatusMessage = '';
      }
    }
  }

  onAgreementToggle(name: keyof typeof this.viewed, event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked && !this.viewed[name]) {
      this.agreeErrors[name] = 'Please open and read this document before agreeing.';
      input.checked = false;
      this.createSubmitForm.get(name)?.setValue(false, { emitEvent: false });
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (checked) {
      if (name === 'termsOfService') this.is_read_tos = true;
      if (name === 'privacyNotice') this.is_read_pl = true;
    }

    if (!checked) {
      this.agreeErrors[name] = '';
      if (name === 'termsOfService') this.is_read_tos = false;
      if (name === 'privacyNotice') this.is_read_pl = false;
    }
  }

  openTermsOfService(e?: Event): void {
    e?.preventDefault();
    this.currentAgreementOpened = 'termsOfService';
    this.modalTitle = this.translate.instant('onboarding.agreement.terms_of_service');
    this.modalTpl = this.tosTpl;

    if (this.config.useOverlayPolicies && this.policyOverlay) {
      this.policyOverlay.contentTpl = this.modalTpl;
      this.policyOverlay.title = this.modalTitle;
      this.policyOverlay.openModal();
    } else {
      this.policyModal.contentTpl = this.modalTpl;
      this.policyModal.title = this.modalTitle;
      this.policyModal.openModal();
    }
  }

  openPrivacyNotice(e?: Event): void {
    e?.preventDefault();
    this.currentAgreementOpened = 'privacyNotice';
    this.modalTitle = 'Privacy Notice ("Notice")';
    this.modalTpl = this.ppTpl;

    if (this.config.useOverlayPolicies && this.policyOverlay) {
      this.policyOverlay.contentTpl = this.modalTpl;
      this.policyOverlay.title = this.modalTitle;
      this.policyOverlay.openModal();
    } else {
      this.policyModal.contentTpl = this.modalTpl;
      this.policyModal.title = this.modalTitle;
      this.policyModal.openModal();
    }
  }

  onAgreementScrolledToEnd(): void {
    // Enables the "I Agree" button in the modal.
  }

  onAgreementAgreed(): void {
    if (this.currentAgreementOpened === 'privacyNotice') {
      this.markViewed('privacyNotice');
      this.is_read_pl = true;
      this.createSubmitForm.get('privacyNotice')?.setValue(true, { emitEvent: false });
      // Consent gate: accepting the Privacy Notice grants first-party GA4 consent
      // for the main SSR site. Releases any buffered page_view in AppTrackingService.
      this.appConsent?.grant();
    } else if (this.currentAgreementOpened === 'termsOfService') {
      this.markViewed('termsOfService');
      this.is_read_tos = true;
      this.createSubmitForm.get('termsOfService')?.setValue(true, { emitEvent: false });
    }

    if (this.viewed.privacyNotice && this.viewed.termsOfService) {
      this.createSubmitForm.get('userAgreement')?.setValue(true, { emitEvent: false });
      this.is_read_tos = true;
      this.is_read_pl = true;
    }

    this.updateAgreementError();
  }

  onAgreementModalClosed(): void {
    this.modalTpl = null;

    const modal = this.config.useOverlayPolicies ? this.policyOverlay : this.policyModal;
    if (this.currentAgreementOpened && !modal?.hasScrolledToEnd) {
      const name = this.currentAgreementOpened === 'privacyNotice' ? 'Privacy Notice' : 'Terms of Service';
      const alreadyViewed = this.currentAgreementOpened === 'privacyNotice'
        ? this.viewed.privacyNotice
        : this.viewed.termsOfService;
      if (!alreadyViewed) {
        this.agreementStatusMessage = `<span class="text-muted">Please read the ${name}</span>`;
      }
    }

    this.currentAgreementOpened = null;
    this.updateAgreementError();
  }

  openConsentAuthorisation(e?: Event): void {
    // Removed - Consent Authorisation no longer required
  }

  onUserAgreementToggle(event: Event): void {
    const input = event.target as HTMLInputElement;
    const checked = input.checked;

    if (checked) {
      const notViewedList: string[] = [];
      if (!this.viewed.privacyNotice) notViewedList.push('Privacy Notice');
      if (!this.viewed.termsOfService) notViewedList.push('Terms of Service');

      if (notViewedList.length === 0) {
        this.createSubmitForm.patchValue({
          userAgreement: true,
          privacyNotice: true,
          termsOfService: true,
        }, { emitEvent: false });

        this.is_read_tos = true;
        this.is_read_pl = true;

        this.userAgreementError = '';
        return;
      }

      this.userAgreementError = `Please read and agree to the ${notViewedList.join(' and ')}.`;

      input.checked = false;
      this.createSubmitForm.get('userAgreement')?.setValue(false, { emitEvent: false });

      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.userAgreementError = '';
  }

  // ========== Search helper ==========

  public onSearch(type: string, term: { term: string; items: any[] }): void {
    const q = (term.term ?? '').trim();
    const needle = this.search.norm(q);
    switch (type) {
      case 'country':
        if (!q) {
          this.countriesFiltered = [...this.countries];
          return;
        }
        this.countriesFiltered = this.countries.filter(c => {
          const name = this.search.norm(c.name);
          const code = this.search.norm(c.iso2);
          return name.includes(needle) || code.startsWith(needle);
        });
        return;
      case 'stateSelected':
        if (!q) {
          this.statesListFiltered = [...this.statesList];
          return;
        }
        this.statesListFiltered = this.statesList.filter(s => {
          return this.search.norm(s.name).includes(needle);
        });
        return;
    }
  }
}
